import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface HighlightItem {
  id: string;
  type: "image" | "video";
  media_url: string;
  caption: string | null;
  display_order: number;
}

interface Highlight {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  display_order: number;
  is_active: boolean;
  items: HighlightItem[];
}

// Fallback data when no database entries exist
const fallbackHighlights: Highlight[] = [
  {
    id: "cocktail",
    title: "Cocktail Hours",
    description: "Set the perfect ambiance as guests arrive with sophisticated live music",
    cover_image_url: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&h=400&fit=crop",
    display_order: 1,
    is_active: true,
    items: [
      {
        id: "ck1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=1080&h=1920&fit=crop",
        caption: "Setting the perfect ambiance",
        display_order: 1,
      },
    ],
  },
  {
    id: "ceremonial",
    title: "Ceremonial Music",
    description: "Make your special moments memorable with our serenading performances",
    cover_image_url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=400&fit=crop",
    display_order: 2,
    is_active: true,
    items: [
      {
        id: "ce1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1080&h=1920&fit=crop",
        caption: "Beautiful ceremony moments",
        display_order: 1,
      },
    ],
  },
  {
    id: "corporate",
    title: "Corporate Events",
    description: "Elevate product launches, mixers, and company celebrations",
    cover_image_url: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=400&fit=crop",
    display_order: 3,
    is_active: true,
    items: [
      {
        id: "c1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1080&h=1920&fit=crop",
        caption: "Product launch entertainment",
        display_order: 1,
      },
    ],
  },
  {
    id: "weddings",
    title: "Weddings",
    description: "From ceremony to reception, we create the soundtrack to your love story",
    cover_image_url: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=400&h=400&fit=crop",
    display_order: 4,
    is_active: true,
    items: [
      {
        id: "w1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1080&h=1920&fit=crop",
        caption: "Beautiful first dance moments",
        display_order: 1,
      },
    ],
  },
  {
    id: "dancefloor",
    title: "Dancefloor Hype",
    description: "Keep the energy high with live accompaniment that moves the crowd",
    cover_image_url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop",
    display_order: 5,
    is_active: true,
    items: [
      {
        id: "d1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1080&h=1920&fit=crop",
        caption: "High-energy performances",
        display_order: 1,
      },
    ],
  },
  {
    id: "specialty",
    title: "Specialty Services",
    description: "Indian instruments including shehnai, dhol, tabla, and more",
    cover_image_url: "https://images.unsplash.com/photo-1583089892943-e02e5b017b6a?w=400&h=400&fit=crop",
    display_order: 6,
    is_active: true,
    items: [
      {
        id: "s1",
        type: "image",
        media_url: "https://images.unsplash.com/photo-1583089892943-e02e5b017b6a?w=1080&h=1920&fit=crop",
        caption: "Traditional shehnai performances",
        display_order: 1,
      },
    ],
  },
];

export function HighlightsStory() {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewedHighlights, setViewedHighlights] = useState<Set<string>>(new Set());
  const [activeHighlight, setActiveHighlight] = useState<Highlight | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const IMAGE_DURATION = 5000; // 5 seconds for images
  const VIDEO_DURATION = 20000; // 20 seconds for videos

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      const { data: highlightsData, error: highlightsError } = await supabase
        .from("highlights")
        .select("*")
        .eq("is_active", true)
        .order("display_order");

      if (highlightsError) throw highlightsError;

      const { data: itemsData, error: itemsError } = await supabase
        .from("highlight_items")
        .select("*")
        .order("display_order");

      if (itemsError) throw itemsError;

      const highlightsWithItems: Highlight[] = (highlightsData || [])
        .map((h) => ({
          id: h.id,
          title: h.title,
          description: h.description,
          cover_image_url: h.cover_image_url,
          display_order: h.display_order,
          is_active: h.is_active,
          items: (itemsData || [])
            .filter((item) => item.highlight_id === h.id)
            .map((item) => ({
              id: item.id,
              type: item.type as "image" | "video",
              media_url: item.media_url,
              caption: item.caption,
              display_order: item.display_order,
            })),
        }))
        .filter((h) => h.items.length > 0 || h.cover_image_url);

      // Use database data if available, otherwise fallback
      if (highlightsWithItems.length > 0) {
        setHighlights(highlightsWithItems);
      } else {
        setHighlights(fallbackHighlights);
      }
    } catch (error) {
      console.error("Error fetching highlights:", error);
      setHighlights(fallbackHighlights);
    } finally {
      setLoading(false);
    }
  };

  const currentItem = activeHighlight?.items[currentItemIndex];

  const goToNextItem = useCallback(() => {
    if (!activeHighlight) return;
    
    if (currentItemIndex < activeHighlight.items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
      setProgress(0);
    } else {
      // Move to next highlight or close
      const currentHighlightIndex = highlights.findIndex(h => h.id === activeHighlight.id);
      if (currentHighlightIndex < highlights.length - 1) {
        const nextHighlight = highlights[currentHighlightIndex + 1];
        if (nextHighlight.items.length > 0) {
          setActiveHighlight(nextHighlight);
          setCurrentItemIndex(0);
          setProgress(0);
          setViewedHighlights(prev => new Set([...prev, nextHighlight.id]));
        } else {
          setActiveHighlight(null);
        }
      } else {
        setActiveHighlight(null);
      }
    }
  }, [activeHighlight, currentItemIndex, highlights]);

  const goToPrevItem = useCallback(() => {
    if (!activeHighlight) return;
    
    if (currentItemIndex > 0) {
      setCurrentItemIndex(prev => prev - 1);
      setProgress(0);
    } else {
      // Move to previous highlight
      const currentHighlightIndex = highlights.findIndex(h => h.id === activeHighlight.id);
      if (currentHighlightIndex > 0) {
        const prevHighlight = highlights[currentHighlightIndex - 1];
        if (prevHighlight.items.length > 0) {
          setActiveHighlight(prevHighlight);
          setCurrentItemIndex(prevHighlight.items.length - 1);
          setProgress(0);
        }
      }
    }
  }, [activeHighlight, currentItemIndex, highlights]);

  // Auto-advance timer
  useEffect(() => {
    if (!activeHighlight || isPaused || !currentItem) return;

    const duration = currentItem.type === "video" ? VIDEO_DURATION : IMAGE_DURATION;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          goToNextItem();
          return 0;
        }
        return prev + (100 / (duration / 100));
      });
    }, 100);

    return () => clearInterval(interval);
  }, [activeHighlight, isPaused, goToNextItem, currentItem]);

  const openHighlight = (highlight: Highlight) => {
    if (highlight.items.length === 0) return;
    setActiveHighlight(highlight);
    setCurrentItemIndex(0);
    setProgress(0);
    setViewedHighlights(prev => new Set([...prev, highlight.id]));
  };

  const closeViewer = () => {
    setActiveHighlight(null);
    setCurrentItemIndex(0);
    setProgress(0);
  };

  const handleTouchStart = () => {
    setIsPaused(true);
  };

  const handleTouchEnd = () => {
    setIsPaused(false);
  };

  const handleAreaClick = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    if (x < width / 3) {
      goToPrevItem();
    } else if (x > (width * 2) / 3) {
      goToNextItem();
    }
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="rounded-xl bg-card border border-border p-6 animate-pulse">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-muted rounded w-24" />
                <div className="h-4 bg-muted rounded w-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Highlights Grid with Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {highlights.map((highlight) => (
          <button
            key={highlight.id}
            onClick={() => openHighlight(highlight)}
            disabled={highlight.items.length === 0}
            className={cn(
              "group relative overflow-hidden rounded-xl bg-card border border-border p-6 text-left transition-all duration-300",
              highlight.items.length > 0 
                ? "hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 cursor-pointer"
                : "opacity-60 cursor-not-allowed"
            )}
          >
            {/* Story indicator ring */}
            <div className="flex items-start gap-4 mb-4">
              <div
                className={cn(
                  "flex-shrink-0 w-16 h-16 rounded-full p-[3px] transition-all duration-300",
                  viewedHighlights.has(highlight.id)
                    ? "bg-muted-foreground/30"
                    : highlight.items.length > 0
                      ? "bg-gradient-to-tr from-primary via-primary/80 to-primary/60"
                      : "bg-muted"
                )}
              >
                <div className="w-full h-full rounded-full p-[2px] bg-card">
                  {highlight.cover_image_url ? (
                    <img
                      src={highlight.cover_image_url}
                      alt={highlight.title}
                      className="w-full h-full rounded-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-muted flex items-center justify-center">
                      <span className="text-2xl">🎵</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-primary group-hover:text-primary/80 transition-colors">
                  {highlight.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {highlight.description}
                </p>
              </div>
            </div>
            
            {/* Tap to view indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground/70">
              {highlight.items.length > 0 ? (
                <>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    Tap to view highlights
                  </span>
                  <span>•</span>
                  <span>{highlight.items.length} {highlight.items.length === 1 ? "story" : "stories"}</span>
                </>
              ) : (
                <span>Coming soon</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Fullscreen Story Viewer */}
      <Dialog open={!!activeHighlight} onOpenChange={(open) => !open && closeViewer()}>
        <DialogContent 
          className="max-w-lg w-full h-[100dvh] sm:h-[90vh] p-0 bg-black border-0 overflow-hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {activeHighlight && currentItem && (
            <div 
              className="relative w-full h-full flex flex-col"
              onMouseDown={handleTouchStart}
              onMouseUp={handleTouchEnd}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Progress Bars */}
              <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2 pt-3">
                {activeHighlight.items.map((_, idx) => (
                  <div key={idx} className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all duration-100"
                      style={{
                        width: idx < currentItemIndex 
                          ? "100%" 
                          : idx === currentItemIndex 
                            ? `${progress}%` 
                            : "0%",
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Header */}
              <div className="absolute top-8 left-0 right-0 z-20 flex items-center justify-between px-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/50">
                    {activeHighlight.cover_image_url ? (
                      <img
                        src={activeHighlight.cover_image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span>🎵</span>
                      </div>
                    )}
                  </div>
                  <span className="text-white text-sm font-semibold">
                    {activeHighlight.title}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPaused(!isPaused);
                    }}
                    className="p-2 text-white/80 hover:text-white transition-colors"
                  >
                    {isPaused ? <Play size={20} /> : <Pause size={20} />}
                  </button>
                  {currentItem.type === "video" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMuted(!isMuted);
                      }}
                      className="p-2 text-white/80 hover:text-white transition-colors"
                    >
                      {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeViewer();
                    }}
                    className="p-2 text-white/80 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Content Area */}
              <div 
                className="flex-1 relative overflow-hidden cursor-pointer"
                onClick={handleAreaClick}
              >
                {currentItem.type === "image" ? (
                  <img
                    src={currentItem.media_url}
                    alt={currentItem.caption || ""}
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                ) : (
                  <video
                    src={currentItem.media_url}
                    autoPlay
                    loop
                    muted={isMuted}
                    playsInline
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                )}
              </div>

              {/* Navigation Arrows (Desktop) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevItem();
                }}
                className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextItem();
                }}
                className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <ChevronRight size={24} />
              </button>

              {/* Caption */}
              {currentItem.caption && (
                <div className="absolute bottom-0 left-0 right-0 z-20 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <p className="text-white text-center text-sm sm:text-base font-medium">
                    {currentItem.caption}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
